import React, { useState, useRef, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
  Alert,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import DocumentPicker, { types } from "react-native-document-picker";
import Svg, { Path } from "react-native-svg";

import { reportError } from "../../utils/Instrumentation";

interface AttachmentUploaderProps {
  onFileUpload: (file: File | null, fileUrl?: string) => void;
  currentFileName?: string | null;
  isCompleted?: boolean; // Whether the walk is completed (for styling)
}

const AttachmentUploader: React.FC<AttachmentUploaderProps> = ({
  onFileUpload,
  currentFileName,
  isCompleted = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(
    currentFileName || null
  );
  const [isDragActive, setIsDragActive] = useState(false);
  const dropZoneId = "attachment-drop-zone";

  const validateFile = (file: any): boolean => {
    // Check file size (5MB limit)
    if (file.size > 10 * 1024 * 1024) {
      Alert.alert("File Too Large", "Please select a file smaller than 5MB.");
      return false;
    }

    // Check file type - accept jpeg and png only. If MIME type missing, fallback to file extension.
    const acceptTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const fileTypeOk = file.type ? acceptTypes.includes(file.type) : undefined;
    if (fileTypeOk === false) {
      Alert.alert('Invalid file type', 'Please select a JPEG or PNG image.');
      return false;
    }

    if (fileTypeOk === undefined) {
      // Fallback: check extension
      const name = (file.name || '').toLowerCase();
      if (!name.endsWith('.jpg') && !name.endsWith('.jpeg') && !name.endsWith('.png')) {
        Alert.alert('Invalid file type', 'Please select a JPEG or PNG image.');
        return false;
      }
    }

    return true;
  };

  const handleFileSelection = async () => {
    setIsUploading(true);

    try {
  if (Platform.OS === "web") {
        // For web platform
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = "image/jpeg,image/jpg,image/png";

        const fileSelectedPromise = new Promise<File | null>((resolve, reject) => {
          fileInput.onchange = (event) => {
            const target = event.target as HTMLInputElement;
            const files = target.files;
            if (files && files.length > 0) {
              resolve(files[0]);
            } else {
              resolve(null);
            }
          };

          // Handle cancel event - when user closes the dialog without selecting
          fileInput.oncancel = () => {
            resolve(null);
          };
        });

        fileInput.click();
        const file = await fileSelectedPromise;
        
        if (!file) {
          // User cancelled or didn't select a file
          console.log("User cancelled file picker or no file selected");
          setIsUploading(false);
          setIsDragActive(false);
          return;
        }
        
        if (validateFile(file)) {
          setFileName(file.name);
          const fileUrl = URL.createObjectURL(file);
          onFileUpload(file, fileUrl);
        }
        setIsDragActive(false);
      } else {
        // For iOS/Android platforms using react-native-document-picker
        const result = await DocumentPicker.pick({
          type: [types.images, "public.jpeg", "public.png"],
          copyTo: "cachesDirectory",
        });

        const pickedFile = Array.isArray(result) ? result[0] : result;

        if (validateFile(pickedFile)) {
          console.log("File picked:", pickedFile.name);
          setFileName(pickedFile.name);

          const fileObject = {
            name: pickedFile.name,
            type: pickedFile.type,
            uri: pickedFile.uri,
            size: pickedFile.size,
          } as any;

          onFileUpload(fileObject, pickedFile.uri);
        }
      }
    } catch (error) {
      if (DocumentPicker.isCancel(error)) {
        console.log("User cancelled file picker");
        // Reset uploading state when user cancels
        setIsUploading(false);
        return;
      } else {
        console.error("Error uploading file:", error);
        reportError({
          severity: "ERROR",
          message: error instanceof Error ? error.message : String(error),
          stackTrace: error instanceof Error ? error.stack : undefined,
        });
        Alert.alert("Error", "Failed to upload file. Please try again.");
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Web drag & drop handlers
  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }

    const node = document.getElementById(dropZoneId);
    if (!node) return;

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      setIsDragActive(true);
    };

    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      setIsDragActive(false);
    };

    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setIsDragActive(false);
      const dt = e.dataTransfer;
      if (dt && dt.files && dt.files.length > 0) {
        const file = dt.files[0];
        if (validateFile(file)) {
          setFileName(file.name);
          const fileUrl = URL.createObjectURL(file);
          onFileUpload(file, fileUrl);
        }
      }
    };

    node.addEventListener('dragover', onDragOver as any);
    node.addEventListener('dragenter', onDragOver as any);
    node.addEventListener('dragleave', onDragLeave as any);
    node.addEventListener('drop', onDrop as any);

    return () => {
      node.removeEventListener('dragover', onDragOver as any);
      node.removeEventListener('dragenter', onDragOver as any);
      node.removeEventListener('dragleave', onDragLeave as any);
      node.removeEventListener('drop', onDrop as any);
    };
    // We intentionally don't include onFileUpload/validateFile in deps to keep
    // these DOM listeners stable. dropZoneRef.current is mutable and not a
    // valid dependency for exhaustive-deps, so suppress the rule for this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearAttachment = () => {
    setFileName(null);
    onFileUpload(null);
  };

  if (fileName) {
    return (
      <View style={styles.attachmentPreview}>
        <View style={styles.fileInfo}>
          <MaterialIcons name="description" size={24} color="#0066CC" />
          <Text
            style={styles.fileName}
            numberOfLines={1}
            ellipsizeMode="middle"
          >
            {fileName}
          </Text>
        </View>
        <TouchableOpacity onPress={clearAttachment} style={styles.removeButton}>
          <MaterialIcons name="close" size={20} color="#6c757d" />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.attachmentsContainer}>
      <View
        nativeID={Platform.OS === "web" ? dropZoneId : undefined}
        testID={Platform.OS === "web" ? dropZoneId : undefined}
        style={[
          styles.dropZone,
          Platform.OS === "web" && isDragActive && styles.dropZoneActive,
          isCompleted && { backgroundColor: "#D0D0D0" },
        ]}
      >
        <View style={styles.iconLabelContainer}>
          <View style={styles.iconContainer}>
            <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
              <Path
                d="M21.8333 2.66667V21.3333H3.16667V2.66667H21.8333ZM21.8333 0H3.16667C1.7 0 0.5 1.2 0.5 2.66667V21.3333C0.5 22.8 1.7 24 3.16667 24H21.8333C23.3 24 24.5 22.8 24.5 21.3333V2.66667C24.5 1.2 23.3 0 21.8333 0ZM15.3533 11.8133L11.3533 16.9733L8.5 13.52L4.5 18.6667H20.5L15.3533 11.8133Z"
                fill={isCompleted ? "#6C757D" : "#3A3A3A"}
              />
            </Svg>
          </View>
          <Text style={[
            styles.dragDropText,
            isCompleted && { color: "#6C757D" }
          ]}>Drag and drop your file here</Text>
          <Text style={[
            styles.dragDropText,
            isCompleted && { color: "#6C757D" }
          ]}>(only .jpg,.png)</Text>
        </View>
        <View style={{ marginTop: 8 }}>
          <TouchableOpacity
            testID="lh-button"
            style={[
              styles.uploadButton,
              isCompleted && { backgroundColor: "#D0D0D0", borderColor: "#A0A0A0" }
            ]}
            onPress={() => handleFileSelection()}
            disabled={isUploading || isCompleted}
          >
            <View style={styles.buttonContent}>
              <View style={styles.uploadIconContainer}>
                <Svg width="10" height="12" viewBox="0 0 10 12" fill="none">
                  <Path
                    d="M3 8.66667H7V4.66667H9.66667L5 0L0.333336 4.66667H3V8.66667ZM5 1.88667L6.44667 3.33333H5.66667V7.33333H4.33334V3.33333H3.55334L5 1.88667ZM0.333336 10H9.66667V11.3333H0.333336V10Z"
                    fill={isCompleted ? "#6C757D" : "#005CBC"}
                  />
                </Svg>
              </View>
              <Text
                testID={isUploading ? "loading-text" : "button-text"}
                style={[
                  styles.uploadButtonText,
                  isCompleted && { color: "#6C757D" }
                ]}
              >
                {isUploading ? "Uploading..." : "Upload file"}
              </Text>
            </View>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  attachmentsContainer: {
    width: "100%",
  },
  dropZone: {
    width: "100%",
    // On web we want the drop zone to fill the available width and not be cropped.
    // Remove overly restrictive maxWidth and provide a reasonable minHeight so
    // the dashed container has a similar visual height to iOS/iPad.
    maxWidth: undefined,
    paddingHorizontal: 16,
    paddingVertical: 0,
    alignItems: "center",
    justifyContent: Platform.OS === "web" ? "center" : undefined,
    minHeight: Platform.OS === "web" ? 110 : undefined,
    // gap is not supported consistently in React Native, use margins instead where needed
    backgroundColor: "transparent",
  },
  iconLabelContainer: {
    width: "100%",
    alignItems: "center",
    // spacing between icon and labels uses margin on children for compatibility
  },
  iconContainer: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    marginTop: Platform.OS === "ios" ? 8 : 8,
  },
  dragDropText: {
    fontFamily: "Inter",
    fontWeight: "400",
    fontSize: 12,
    lineHeight: 18, // 1.5em
    textAlign: "center",
    color: "#3A3A3A",
    width: "100%",
    marginTop: 4,
  },
  uploadButton: {
    height: 24,
    backgroundColor: "#FBFCFE",
    borderWidth: 1,
    borderColor: "#005CBC",
    borderRadius: 24,
    alignSelf: "stretch",
  },
  buttonContent: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 2,
    paddingHorizontal: 8,
    paddingVertical: 0,
  },
  uploadIconContainer: {
    width: 16,
    height: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  uploadButtonText: {
    fontFamily: "Inter",
    fontWeight: "700",
    fontSize: 12,
    lineHeight: 18, // 1.5em
    textAlign: "center",
    color: "#005CBC",
  },
  attachmentPreview: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: Platform.OS === "web" ? 12 : 16,
    borderWidth: 1,
    borderColor: "#e9ecef",
    borderRadius: 8,
    backgroundColor: "#f8f9fa",
    width: "100%",
    minHeight: Platform.OS === "ios" ? 60 : 50,
  },
  fileInfo: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  fileName: {
    marginLeft: 8,
    fontSize: Platform.OS === "web" ? 14 : 16,
    color: "#212529",
    flex: 1,
  },
  removeButton: {
    padding: Platform.OS === "web" ? 4 : 8,
    marginLeft: 8,
  },
  dropZoneActive: {
    borderColor: "#005CBC",
    backgroundColor: "rgba(0,92,188,0.04)",
  },
});

export default AttachmentUploader;
